import { init, addIntegration, browserTracingIntegration, getReplay, vueIntegration } from '@sentry/vue';

import { dropNetworkErrors, isReplayBlockedPath, scrubBreadcrumbUrls, scrubSensitiveUrls } from '@/wabunka-src/common/utils/sentry';
import { sanitizeSensitiveQuery } from '@/wabunka-src/common/utils/url/sanitizeSensitiveQuery';

// @sentry/vue は Integration 型を再エクスポートしないため、宣言済みの依存から引く
type SentryIntegration = Parameters<typeof addIntegration>[0];

// integration の processEvent は client 側に載り init() ごとに作り直される。addEventProcessor は
// isolation scope に積まれ init() でも消えないため、plugin が毎リクエスト走る SSR で累積する
const scrubSensitiveUrlsIntegration = (): SentryIntegration => ({
  name: 'ScrubSensitiveUrls',
  processEvent: scrubSensitiveUrls,
});

const lazyLoadIntegrations = async () => {
  // avoid loading on server side
  if (!process.client) {
    return;
  }
  const { replayIntegration } = await import('@sentry/vue');

  addIntegration(
    replayIntegration({
      maskAllText: false,
      maskAllInputs: true,
      mask: ['.mask-me'],
      // Replay の navigation / resource スパンは PerformanceObserver 由来で積まれ breadcrumb を
      // 経由しないため beforeBreadcrumb では覆えない。description にドキュメント URL 全体が入る。
      //
      // このフックは rrweb の Custom イベント（type 5）にしか適用されない。他の記録経路は
      // scrubSensitiveUrls と isReplayBlockedPath による記録停止で塞ぐ。
      beforeAddRecordingEvent: (event) => {
        if (event.data?.tag === 'performanceSpan' && typeof event.data.payload?.description === 'string') {
          event.data.payload.description = sanitizeSensitiveQuery(event.data.payload.description);
        }
        return event;
      },
    }),
  );
};

export default defineNuxtPlugin(async ({ vueApp }) => {
  const runtimeConfig = useRuntimeConfig();
  const router = useRouter();
  const isProduction = runtimeConfig.public.env === 'production' || runtimeConfig.public.env === 'staging';

  init({
    dsn: runtimeConfig.public.SENTRY_DSN as string,
    app: vueApp,
    environment: isProduction ? 'production' : 'development',
    enabled: runtimeConfig.public.env !== 'local',
    // Capture Replay for 10% of all sessions
    replaysSessionSampleRate: 0.05,
    // // Capture 100% of sessions with an error
    replaysOnErrorSampleRate: 1.0,

    // Capture 10% of transactions
    tracesSampleRate: 0.1,
    integrations: [
      browserTracingIntegration({
        router,
        enableInp: true,
      }),
      vueIntegration({
        tracingOptions: {
          trackComponents: true,
        },
      }),
      // 解除リンクの識別子等をイベントの URL から落とす。unsubscribeToken は所持しているだけで他人の購読を
      // 停止できるケイパビリティであり、第三者かつ長期保持のサービスに残してはならない。
      scrubSensitiveUrlsIntegration(),
    ],
    ignoreErrors: [
      "Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing.",
      // useAsyncData の dedupe: 'cancel'（デフォルト）が進行中リクエストを中断した際の AbortError。
      // unhandledrejection 経由で届くが Nuxt 仕様上の正常動作でユーザー影響なしのため無視する。
      'AsyncData request cancelled by deduplication',
    ],
    beforeSend: dropNetworkErrors,
    beforeBreadcrumb: scrubBreadcrumbUrls,
  });

  // 一度 stop() すると記録は戻らない。start() での再開は sample rate を無視して必ず記録するため採らない
  const stopReplayIfBlocked = (path: string) => {
    if (!isReplayBlockedPath(path)) return;
    void getReplay()?.stop();
  };

  // 対象パスでは integration ごと足さない。addIntegration はサンプル対象セッションで同期的に
  // 記録を開始し、rrweb の Meta イベントにドキュメント URL がそのまま載る。あとから stop() を
  // 呼んでも記録済みセグメントを flush して送るため、開始させないことでしか塞げない
  let replayRequested = false;
  const startReplayUnlessBlocked = (path: string) => {
    if (replayRequested || isReplayBlockedPath(path)) return;
    replayRequested = true;
    lazyLoadIntegrations().catch((e) => console.error(e));
  };

  startReplayUnlessBlocked(router.currentRoute.value.path);
  router.afterEach((to) => {
    startReplayUnlessBlocked(to.path);
    stopReplayIfBlocked(to.path);
  });
});
